import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap';

import Loading from '../Loading';
import SettingsSidebar from './SettingsSidebar';
import SubjectForm from './SubjectForm';
import SettingsPlaceholder from './SettingsPlaceholder';

import {
  getMenu,
  getSubject,
  saveSubject,
} from '../../services/settings.service';
import {
  flattenFields,
  validateChanges,
  formatApiError,
  isApprovalHandled,
  isForbidden,
  isNotFound,
} from './fieldUtils';
import AccessDenied from '../AccessDenied';
import { showError, showInfo } from '../../actions/notificationActions';

import '../../assets/css/slider.css';

// Returns a copy of the loaded subject with the saved values applied to their
// fields (and marked persisted), so the form reflects the write without a re-read.
const applySavedChanges = (subjectData, changed) => {
  if (!subjectData) return subjectData;

  const saved = changed.reduce((map, change) => {
    map[change.key] = change.value;
    return map;
  }, {});

  return {
    ...subjectData,
    subject: {
      ...subjectData.subject,
      groups: (subjectData.subject.groups || []).map((group) => ({
        ...group,
        fields: (group.fields || []).map((field) =>
          Object.prototype.hasOwnProperty.call(saved, field.key)
            ? { ...field, value: saved[field.key], persisted: true }
            : field
        ),
      })),
    },
  };
};

class Settings extends Component {
  state = {
    menu: [],
    permissions: {},
    selected: null, // { section, subject }
    subjectData: null,
    changed: [], // [{ key, value }]
    loading: false,
    loadingSubject: false,
    saving: false,
    saveError: null,
    accessDenied: false, // the caller lacks application-config read access
    pendingSelection: null, // { section, subject } awaiting unsaved-edits confirmation
    // Set when a deep link resolved to a subject the facade no longer has, and
    // the menu had not arrived yet to redirect against.
    awaitingFallback: false,
  };

  componentDidMount() {
    this.loadMenu();
    // Restore the view from the URL on first load / refresh.
    const selection = this.getSelectionFromUrl();
    if (selection) {
      this.loadSubject(selection.section, selection.subject);
    }
  }

  componentDidUpdate(prevProps) {
    // The URL is the source of truth — react to address-bar / back-forward changes.
    if (prevProps.location.pathname === this.props.location.pathname) return;

    const selection = this.getSelectionFromUrl();
    if (!selection) {
      this.setState({ selected: null, subjectData: null, changed: [] });
      return;
    }

    const { selected } = this.state;
    if (
      !selected ||
      selected.section !== selection.section ||
      selected.subject !== selection.subject
    ) {
      this.loadSubject(selection.section, selection.subject);
    }
  }

  // Parses `/settings/:section/:subject` from the current location.
  getSelectionFromUrl() {
    const parts = this.props.location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'settings' && parts[1] && parts[2]) {
      return { section: parts[1], subject: parts[2] };
    }
    return null;
  }

  navigateToSubject(section, subject) {
    this.props.history.push(`/settings/${section}/${subject}`);
  }

  loadMenu() {
    this.setState({ loading: true });
    getMenu()
      .then((res) => {
        const menu = res.data || [];
        this.setState(
          {
            loading: false,
            // Permissions live on the menu response only — never on a subject —
            // so this is read once and applied to every subject navigated to.
            permissions: (res.meta && res.meta.permissions) || {},
            menu,
          },
          () => {
            if (this.state.awaitingFallback) this.fallBackToFirstSubject();
          }
        );
      })
      .catch((err) => {
        // Every /config route is ACL-guarded now, so this is reachable by any
        // role that deep-links here, or whose access was revoked mid-session.
        // Show the state, not a toast — there is nothing for them to retry.
        if (isForbidden(err)) {
          this.setState({ loading: false, accessDenied: true });
          return;
        }
        this.setState({ loading: false });
        this.props.dispatch(showError(err));
      });
  }

  // The facade's sections and subjects churn, so a bookmarked subject can simply
  // stop existing. Land the user on the first section instead of erroring out.
  fallBackToFirstSubject() {
    const { menu } = this.state;
    const section = menu[0];
    const subject = section && (section.subjects || [])[0];

    this.setState({ awaitingFallback: false });
    if (!section || !subject) {
      // Nothing to fall back to — leave the placeholder showing.
      this.setState({ selected: null, subjectData: null });
      this.props.history.replace('/settings');
      return;
    }
    this.navigateToSubject(section.key, subject.key);
  }

  loadSubject(section, subject) {
    this.setState({
      selected: { section, subject },
      loadingSubject: true,
      changed: [],
      saveError: null,
      subjectData: null,
    });
    getSubject(section, subject)
      .then((res) => {
        this.setState({ loadingSubject: false, subjectData: res.data });
      })
      .catch((err) => {
        this.setState({ loadingSubject: false });

        // The menu request raced this one and is reporting the same denial.
        if (isForbidden(err)) {
          this.setState({ accessDenied: true });
          return;
        }

        if (isNotFound(err)) {
          this.props.dispatch(
            showError(
              'That settings subject no longer exists — showing the first section instead.'
            )
          );
          // The menu may still be in flight; loadMenu picks this up when it lands.
          if (this.state.menu.length) {
            this.fallBackToFirstSubject();
          } else {
            this.setState({ awaitingFallback: true });
          }
          return;
        }

        this.props.dispatch(showError(formatApiError(err)));
      });
  }

  handleSelect = (section, subject) => {
    const { selected, changed } = this.state;
    if (
      selected &&
      selected.section === section &&
      selected.subject === subject
    ) {
      return;
    }
    if (changed.length > 0) {
      // Defer the switch until the user confirms losing their unsaved edits.
      this.setState({ pendingSelection: { section, subject } });
      return;
    }
    this.navigateToSubject(section, subject);
  };

  confirmSwitch = () => {
    const { pendingSelection } = this.state;
    // Drop edits up front so the URL-driven reload doesn't see them as unsaved.
    this.setState({ pendingSelection: null, changed: [] });
    if (pendingSelection) {
      this.navigateToSubject(
        pendingSelection.section,
        pendingSelection.subject
      );
    }
  };

  cancelSwitch = () => {
    this.setState({ pendingSelection: null });
  };

  handleFieldChange = (key, value) => {
    this.setState((state) => ({
      changed: state.changed
        .filter((c) => c.key !== key)
        .concat({ key, value }),
    }));
  };

  handleReset = () => {
    this.setState({ changed: [], saveError: null });
  };

  handleSave = () => {
    const { selected, changed, subjectData, permissions } = this.state;

    // The form hides its buttons in read-only mode; this is the backstop that
    // guarantees no write leaves the page.
    if (permissions.canUpdate === false) return;

    const error = validateChanges(flattenFields(subjectData), changed);
    if (error) {
      this.setState({ saveError: error });
      this.props.dispatch(showError(error));
      return;
    }

    this.setState({ saving: true, saveError: null });
    saveSubject(selected.section, selected.subject, changed)
      .then((res) => {
        this.props.dispatch(showInfo(res.message));
        // Merge the saved values into the loaded subject so the controls reflect
        // the write immediately. Re-fetching here can race a not-yet-consistent
        // read and snap the controls back to their previous values.
        this.setState((state) => ({
          saving: false,
          changed: [],
          subjectData: applySavedChanges(state.subjectData, changed),
        }));
      })
      .catch((err) => {
        // Roles are re-resolved from the database on every request, so update
        // access can disappear under a page that has been open a while. Flip to
        // read-only rather than letting them keep trying.
        if (isForbidden(err)) {
          this.setState((state) => ({
            saving: false,
            permissions: { ...state.permissions, canUpdate: false },
            saveError: formatApiError(
              err,
              'You no longer have permission to change these settings.'
            ),
          }));
          return;
        }

        // The batch is rejected as a whole, so keep the edits for another go.
        this.setState({
          saving: false,
          saveError: isApprovalHandled(err)
            ? null
            : formatApiError(err, 'Could not save these settings.'),
        });
        this.props.dispatch(showError(err));
      });
  };

  renderContent() {
    const {
      selected,
      subjectData,
      changed,
      loadingSubject,
      saving,
      saveError,
      permissions,
    } = this.state;

    if (loadingSubject) {
      return (
        <div
          className="justify-content-center text-center"
          style={{ marginTop: 60 }}
        >
          <Loading />
        </div>
      );
    }

    if (!selected || !subjectData) {
      return <SettingsPlaceholder />;
    }

    return (
      <SubjectForm
        subjectData={subjectData}
        changed={changed}
        saving={saving}
        saveError={saveError}
        canUpdate={permissions.canUpdate}
        onFieldChange={this.handleFieldChange}
        onSave={this.handleSave}
        onReset={this.handleReset}
      />
    );
  }

  render() {
    const { menu, selected, loading, accessDenied, pendingSelection } =
      this.state;

    if (accessDenied) {
      return (
        <div className="content">
          <AccessDenied
            title="You do not have access to application configuration"
            body="Global settings are limited to Super Admin and Technical Support. If you need to change a service's own settings, open that service and use its Settings tab."
          />
        </div>
      );
    }

    return (
      <div className="content">
        {loading ? (
          <div
            className="justify-content-center text-center"
            style={{ marginTop: 60 }}
          >
            <Loading />
          </div>
        ) : (
          <Row>
            <Col md="3">
              <SettingsSidebar
                menu={menu}
                selected={selected}
                onSelect={this.handleSelect}
              />
            </Col>
            <Col md="9">{this.renderContent()}</Col>
          </Row>
        )}

        <Modal
          isOpen={!!pendingSelection}
          toggle={this.cancelSwitch}
          backdrop="static"
        >
          <ModalHeader toggle={this.cancelSwitch}>Unsaved changes</ModalHeader>
          <ModalBody>
            You have unsaved changes that will be lost. Switch subjects anyway?
          </ModalBody>
          <ModalFooter>
            <Button color="default" outline onClick={this.cancelSwitch}>
              Cancel
            </Button>
            <Button color="primary" onClick={this.confirmSwitch}>
              Discard &amp; switch
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

export default connect()(Settings);
